import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API } from "@/utils/api";
import { toast } from "sonner";
import {
  FileText, Download, Eye, CreditCard, Search, Loader2, Plus, Link as LinkIcon, Check, Pencil, SquarePen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import InvoiceEditDialog from "@/pages/admin/InvoiceEditDialog";
import InvoicePolicySwitch from "@/components/InvoicePolicySwitch";

const STATUS_BADGE = {
  UNPAID: "bg-red-100 text-red-700 border-red-200",
  PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PARTIAL: "bg-amber-100 text-amber-700 border-amber-200",
  WAIVED: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminInvoices() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [editInv, setEditInv] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Generate Invoice modal
  const [genOpen, setGenOpen] = useState(false);
  const [genId, setGenId] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  // Manual Invoice modal (no shipment/AWB -- customer-selected, 5% or 18% GST)
  const [manOpen, setManOpen] = useState(false);
  const [manCustQ, setManCustQ] = useState("");
  const [manCustOptions, setManCustOptions] = useState([]);
  const [manCustId, setManCustId] = useState(null);
  const [manCustName, setManCustName] = useState("");
  const [manCustOpen, setManCustOpen] = useState(false);
  const [manDescription, setManDescription] = useState("");
  const [manAmount, setManAmount] = useState("");
  const [manGstRate, setManGstRate] = useState(18);
  const [manBusy, setManBusy] = useState(false);

  // Mark Paid modal
  const [markInv, setMarkInv] = useState(null);
  const [markAmount, setMarkAmount] = useState("");
  const [markRef, setMarkRef] = useState("");
  const [markMethod, setMarkMethod] = useState("BANK_TRANSFER");

  // Customer search typeahead
  const [custQ, setCustQ] = useState("");
  const [custOptions, setCustOptions] = useState([]);
  const [custId, setCustId] = useState(null);
  const [custOpen, setCustOpen] = useState(false);
  // Arrived from a customer page: /admin/invoices?customer_id=... shows only that customer's invoices
  const [urlParams] = useSearchParams();
  useEffect(() => {
    const cid = urlParams.get("customer_id");
    if (!cid) return;
    setCustId(cid);
    api.get(`/v2/customers/profile/${cid}`).then((r) => setCustQ(r.data.company_name || r.data.contact_person || "")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (custId) {
        const r = await api.get(`/v2/invoices/customer/${custId}`);
        setInvoices(Array.isArray(r.data) ? r.data : r.data?.items || []);
      } else {
        // No global list endpoint — try optimistic, fall back to ledger silently
        try {
          const r = await api.get(`/v2/invoices?limit=200`);
          setInvoices(Array.isArray(r.data) ? r.data : r.data?.items || []);
        } catch {
          try {
            const r2 = await api.get(`/v2/shipment-ledger?limit=200`);
            const items = (r2.data?.items || r2.data || []).filter((x) => x.invoice_number);
            setInvoices(items);
          } catch {
            setInvoices([]);
          }
        }
      }
    } catch (e) {
      setInvoices([]);
      toast.error(e.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [custId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = invoices.filter((i) => {
    if (status !== "ALL" && i.payment_status !== status) return false;
    if (from && new Date(i.invoice_date || i.created_at) < new Date(from)) return false;
    if (to && new Date(i.invoice_date || i.created_at) > new Date(to + "T23:59:59"))
      return false;
    if (q) {
      const ql = q.toLowerCase();
      return (
        (i.invoice_number || "").toLowerCase().includes(ql) ||
        (i.awb || "").toLowerCase().includes(ql) ||
        (i.carrier_awb || "").toLowerCase().includes(ql) ||
        String(i.order_id || "").toLowerCase().includes(ql) ||
        (i.customer_name || "").toLowerCase().includes(ql)
      );
    }
    return true;
  });

  // Customer typeahead
  useEffect(() => {
    if (!custOpen) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(
          `/v2/customer-profiles/search?q=${encodeURIComponent(custQ)}`
        );
        setCustOptions(r.data?.results || []);
      } catch {
        setCustOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [custQ, custOpen]);

  // Manual-invoice customer typeahead (separate from the list-filter one above)
  useEffect(() => {
    if (!manCustOpen) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(
          `/v2/customer-profiles/search?q=${encodeURIComponent(manCustQ)}`
        );
        setManCustOptions(r.data?.results || []);
      } catch {
        setManCustOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [manCustQ, manCustOpen]);

  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/invoices/${inv.invoice_number}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sendtoo_${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const sendPaymentLink = async (inv) => {
    try {
      const r = await api.post("/v2/payments/links/create", {
        invoice_id: inv.id,
        customer_id: inv.customer_id,
        expiry_hours: 24,
      });
      const link = r.data?.payment_link || r.data?.link_url || r.data?.url;
      if (link) {
        try {
          await navigator.clipboard.writeText(link);
        } catch (err) {
          // clipboard unavailable - that's ok
        }
        toast.success("Payment link created & copied!", {
          description: link.slice(0, 60) + "…",
        });
      } else {
        toast.success("Payment link created");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    }
  };

  const submitMarkPaid = async () => {
    if (!markInv || !markAmount) return;
    try {
      await api.put(`/v2/invoices/${markInv.invoice_number}/mark-paid`, {
        payment_reference: markRef,
        amount: Number(markAmount),
        payment_method: markMethod,
      });
      toast.success("Invoice marked as paid");
      setMarkInv(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const generateInvoice = async () => {
    if (!genId.trim()) return;
    setGenBusy(true);
    try {
      let id = genId.trim();
      // If AWB/LR provided, resolve to shipment id first
      if (!/^[a-f0-9]{24}$/i.test(id)) {
        try {
          const r = await api.get(`/shipments?search=${id}&limit=1`);
          const items = r.data?.items || r.data || [];
          const found = items[0];
          if (found) id = found.id || found._id;
        } catch (err) {
          // best-effort - fall through with original id
        }
      }
      const r = await api.post(`/v2/invoices/generate/${id}`);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setGenOpen(false);
      setGenId("");
      navigate(`/admin/invoices/${r.data.invoice_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate");
    } finally {
      setGenBusy(false);
    }
  };

  const resetManualInvoiceForm = () => {
    setManCustQ("");
    setManCustOptions([]);
    setManCustId(null);
    setManCustName("");
    setManDescription("");
    setManAmount("");
    setManGstRate(18);
  };

  const submitManualInvoice = async () => {
    if (!manCustId || !manDescription.trim() || !manAmount || Number(manAmount) <= 0) return;
    setManBusy(true);
    try {
      const r = await api.post(`/v2/invoices/manual`, {
        customer_id: manCustId,
        description: manDescription.trim(),
        amount: Number(manAmount),
        gst_rate: Number(manGstRate),
      });
      toast.success(`Invoice ${r.data.invoice_number} created (LR ${r.data.lr_number})`);
      setManOpen(false);
      resetManualInvoiceForm();
      navigate(`/admin/invoices/${r.data.invoice_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create manual invoice");
    } finally {
      setManBusy(false);
    }
  };

  const exportCsv = () => {
    const headers = [
      "Invoice #", "Date", "Customer", "Order ID", "AWB", "Amount", "GST Type", "Status",
    ];
    const rows = filtered.map((i) => [
      i.invoice_number,
      new Date(i.invoice_date || i.created_at).toISOString().slice(0, 10),
      i.customer_name || i.customer_id || "",
      i.order_id || "",
      i.carrier_awb || i.awb || "",
      Number(i.total_amount || 0).toFixed(2),
      i.tax_type || "",
      i.payment_status || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="admin-invoices-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Invoices</h1>
            <p className="text-sm text-slate-500">
              Manage shipment invoices, payments and refunds
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <InvoicePolicySwitch isAdmin={isAdmin} />
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            {isAdmin && <Button
              onClick={() => setGenOpen(true)}
              className="bg-slate-800 hover:bg-slate-900 text-white"
              data-testid="generate-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Generate Invoice
            </Button>}
            {isAdmin && <Button
              onClick={() => navigate("/admin/invoices/new")}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="manual-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Create Manual Invoice
            </Button>}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search invoice #, AWB, customer"
              className="pl-8"
              data-testid="invoices-search"
            />
          </div>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40"
            data-testid="invoices-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40"
            data-testid="invoices-to"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-slate-300 rounded h-10 px-2 text-sm bg-white"
            data-testid="invoices-status-filter"
          >
            <option value="ALL">All statuses</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PAID">Paid</option>
            <option value="PARTIAL">Partial</option>
            <option value="WAIVED">Waived</option>
          </select>
          <div className="relative">
            <Input
              value={custQ}
              onFocus={() => setCustOpen(true)}
              onChange={(e) => {
                setCustQ(e.target.value);
                setCustOpen(true);
              }}
              onBlur={() => setTimeout(() => setCustOpen(false), 200)}
              placeholder="Filter by customer"
              className="w-56"
              data-testid="invoices-customer-search"
            />
            {custOpen && custOptions.length > 0 && (
              <div className="absolute top-11 right-0 left-0 max-h-48 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg z-10">
                {custOptions.map((c) => (
                  <button
                    key={c.customer_id}
                    onClick={() => {
                      setCustId(c.customer_id);
                      setCustQ(c.company_name || c.contact_person);
                      setCustOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b last:border-b-0"
                  >
                    <div className="font-semibold">{c.company_name || c.contact_person}</div>
                    <div className="text-slate-500">{c.email}</div>
                  </button>
                ))}
              </div>
            )}
            {custId && (
              <button
                onClick={() => {
                  setCustId(null);
                  setCustQ("");
                }}
                className="absolute right-1 top-1 text-xs text-red-500 px-1.5"
                data-testid="clear-customer-filter"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-60" />
              <p className="text-sm">No invoices found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-invoices-table">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-3">Invoice #</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">AWB / Order</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Due</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-center p-3">GST</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((inv) => (
                    <tr key={inv.id || inv.invoice_number} className="hover:bg-slate-50">
                      <td className="p-3 font-mono text-xs font-semibold">
                        <button
                          onClick={() => navigate(`/admin/invoices/${inv.invoice_number}`)}
                          className="text-blue-600 hover:underline"
                          data-testid={`view-inv-${inv.invoice_number}`}
                        >
                          {inv.invoice_number}
                        </button>
                      </td>
                      <td className="p-3 text-slate-700">{inv.customer_name || "—"}</td>
                      <td className="p-3 font-mono text-xs">
                        <div>{inv.carrier_awb || inv.awb || "—"}</div>
                        {inv.order_id && <div className="text-[10px] text-slate-400">Order {inv.order_id}</div>}
                      </td>
                      <td className="p-3 text-slate-600">
                        {new Date(inv.invoice_date || inv.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-slate-600">
                        {inv.due_date
                          ? new Date(inv.due_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="p-3 text-right font-semibold">
                        ₹{Number(inv.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-center text-xs">{inv.tax_type || "—"}</td>
                      <td className="p-3 text-center">
                        <span
                          className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${
                            STATUS_BADGE[inv.payment_status] || STATUS_BADGE.UNPAID
                          }`}
                        >
                          {inv.payment_status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <IconBtn
                            onClick={() => navigate(`/admin/invoices/${inv.invoice_number}`)}
                            title="View"
                            testid={`view-icon-${inv.invoice_number}`}
                          >
                            <Eye className="w-4 h-4" />
                          </IconBtn>
                          <IconBtn
                            onClick={() => downloadPdf(inv)}
                            title="Download PDF"
                            testid={`pdf-${inv.invoice_number}`}
                          >
                            <Download className="w-4 h-4" />
                          </IconBtn>
                          {(isAdmin || (inv.payment_status || "UNPAID") === "UNPAID") && (
                            <IconBtn
                              onClick={() => setEditInv(inv)}
                              title="Edit name / address"
                              testid={`edit-details-${inv.invoice_number}`}
                            >
                              <SquarePen className="w-4 h-4 text-[#159DFF]" />
                            </IconBtn>
                          )}
                          {isAdmin && inv.invoice_type === "manual" && inv.payment_status === "UNPAID" && (
                            <IconBtn
                              onClick={() => navigate(`/admin/invoices/edit/${inv.invoice_number}`)}
                              title="Edit"
                              testid={`edit-${inv.invoice_number}`}
                            >
                              <Pencil className="w-4 h-4 text-slate-600" />
                            </IconBtn>
                          )}
                          {isAdmin && inv.payment_status !== "PAID" && inv.payment_status !== "WAIVED" && (
                            <>
                              <IconBtn
                                onClick={() => {
                                  setMarkInv(inv);
                                  setMarkAmount(
                                    Math.max(
                                      Number(inv.total_amount || 0) - Number(inv.amount_paid || 0),
                                      0
                                    ).toFixed(2)
                                  );
                                  setMarkRef("");
                                }}
                                title="Mark Paid"
                                testid={`markpaid-${inv.invoice_number}`}
                              >
                                <CreditCard className="w-4 h-4 text-emerald-600" />
                              </IconBtn>
                              <IconBtn
                                onClick={() => sendPaymentLink(inv)}
                                title="Send Payment Link"
                                testid={`paymentlink-${inv.invoice_number}`}
                              >
                                <LinkIcon className="w-4 h-4 text-blue-600" />
                              </IconBtn>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Generate Invoice modal */}
      {genOpen && (
        <Modal onClose={() => setGenOpen(false)} title="Generate Invoice">
          <p className="text-sm text-slate-600 mb-3">
            Enter AWB / LR number or shipment ID. We&apos;ll find it and create the invoice.
          </p>
          <Input
            autoFocus
            value={genId}
            onChange={(e) => setGenId(e.target.value)}
            placeholder="AWB / LR / Shipment ID"
            data-testid="generate-invoice-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setGenOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={generateInvoice}
              disabled={genBusy || !genId.trim()}
              className="bg-slate-800 hover:bg-slate-900 text-white"
              data-testid="generate-invoice-submit"
            >
              {genBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Generate
            </Button>
          </div>
        </Modal>
      )}

      {manOpen && (
        <Modal
          onClose={() => { setManOpen(false); resetManualInvoiceForm(); }}
          title="Create Manual Invoice"
        >
          <p className="text-sm text-slate-600 mb-3">
            For billing not tied to a shipment (e.g. transportation services). A Sendtoo LR
            number and invoice number are generated automatically.
          </p>

          <Field label="Customer">
            {manCustId ? (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                <span className="text-sm text-slate-800">{manCustName}</span>
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  onClick={() => { setManCustId(null); setManCustName(""); }}
                  data-testid="manual-invoice-change-customer"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  autoFocus
                  value={manCustQ}
                  onChange={(e) => { setManCustQ(e.target.value); setManCustOpen(true); }}
                  onFocus={() => setManCustOpen(true)}
                  placeholder="Search customer by name, phone or GSTIN"
                  data-testid="manual-invoice-customer-search"
                />
                {manCustOpen && manCustQ.trim().length >= 2 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto">
                    {manCustOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
                    ) : (
                      manCustOptions.map((c) => (
                        <button
                          key={c.id || c._id || c.customer_id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                          onClick={() => {
                            setManCustId(c.id || c._id || c.customer_id);
                            setManCustName(c.company_name || c.name || "Customer");
                            setManCustOpen(false);
                          }}
                        >
                          {c.company_name || c.name || "Customer"}
                          {c.gst_number ? ` — ${c.gst_number}` : ""}
                        </button>
                      ))
                    )}
                    <a
                      href="/admin/customers/create"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 border-t border-slate-100"
                    >
                      + New Customer (opens in new tab)
                    </a>
                  </div>
                )}
              </div>
            )}
          </Field>

          <div className="mt-3">
            <Field label="Description">
              <Input
                value={manDescription}
                onChange={(e) => setManDescription(e.target.value)}
                placeholder="e.g. Hyderabad to Khammam Transportation"
                data-testid="manual-invoice-description"
              />
            </Field>
          </div>

          <div className="mt-3">
            <Field label="Amount (₹)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={manAmount}
                onChange={(e) => setManAmount(e.target.value)}
                placeholder="0.00"
                data-testid="manual-invoice-amount"
              />
            </Field>
          </div>

          <div className="mt-3">
            <Field label="GST Rate">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setManGstRate(18)}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium ${
                    manGstRate === 18
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid="manual-invoice-gst-18"
                >
                  18% — Logistics
                </button>
                <button
                  type="button"
                  onClick={() => setManGstRate(5)}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium ${
                    manGstRate === 5
                      ? "bg-emerald-700 text-white border-emerald-700"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid="manual-invoice-gst-5"
                >
                  5% — Transportation Service
                </button>
              </div>
            </Field>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => { setManOpen(false); resetManualInvoiceForm(); }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitManualInvoice}
              disabled={manBusy || !manCustId || !manDescription.trim() || !manAmount || Number(manAmount) <= 0}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="manual-invoice-submit"
            >
              {manBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Create Invoice
            </Button>
          </div>
        </Modal>
      )}

      {/* Mark Paid modal */}
      {markInv && (
        <Modal onClose={() => setMarkInv(null)} title={`Mark ${markInv.invoice_number} Paid`}>
          <div className="space-y-3">
            <Field label="Amount Paid">
              <Input
                type="number"
                value={markAmount}
                onChange={(e) => setMarkAmount(e.target.value)}
                data-testid="markpaid-amount"
              />
            </Field>
            <Field label="Payment Reference">
              <Input
                value={markRef}
                onChange={(e) => setMarkRef(e.target.value)}
                placeholder="UTR / Txn ID"
                data-testid="markpaid-reference"
              />
            </Field>
            <Field label="Payment Method">
              <select
                value={markMethod}
                onChange={(e) => setMarkMethod(e.target.value)}
                className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
                data-testid="markpaid-method"
              >
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="CASH">Cash</option>
                <option value="CHEQUE">Cheque</option>
                <option value="OTHER">Other</option>
              </select>
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setMarkInv(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitMarkPaid}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="markpaid-submit"
            >
              Confirm
            </Button>
          </div>
        </Modal>
      )}
    <InvoiceEditDialog invoice={editInv} onClose={() => setEditInv(null)} onSaved={load} />
    </DashboardLayout>
  );
}

function IconBtn({ onClick, title, children, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition"
    >
      {children}
    </button>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl p-5 w-full max-w-md shadow-2xl"
        data-testid="invoice-modal"
      >
        <h3 className="text-lg font-bold text-slate-800 mb-3">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
